import React, { useState, useEffect, useRef } from 'react';
import { messagesApi } from '../services/api';
import { User } from '../contexts/AuthContext';

interface Message {
  id: number;
  fromUserId: number;
  toUserId: number;
  contenido: string;
  tipo: 'mensaje' | 'alerta';
  leido: boolean;
  fechaLeido: string | null;
  prioridad: 'baja' | 'normal' | 'alta' | 'urgente';
  createdAt: string;
  sender: {
    id: number;
    name: string;
    email: string;
    role: string;
  };
  recipient?: {
    id: number;
    name: string;
    email: string;
    role: string;
  };
}

interface Conversation {
  userId: number;
  user: {
    id: number;
    name: string;
    email: string;
    role: string;
  };
  lastMessage: Message;
  unreadCount: number;
  messages: Message[];
}

interface MessagesEmailViewProps {
  user: User;
  onMessageRead?: () => void;
}

const MessagesEmailView: React.FC<MessagesEmailViewProps> = ({ user, onMessageRead }) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [conversationMessages, setConversationMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [replyContent, setReplyContent] = useState('');
  const [sending, setSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadConversations();
    loadUnreadCount();
  }, [user.id]);

  // Auto-scroll al final cuando cambian los mensajes
  useEffect(() => {
    const scrollToBottom = () => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }
    };

    // Ejecutar inmediatamente y con un pequeño delay para asegurar renderizado
    scrollToBottom();
    const timer = setTimeout(scrollToBottom, 100);

    return () => clearTimeout(timer);
  }, [conversationMessages]);

  const loadConversations = async () => {
    try {
      setLoading(true);
      const response = await messagesApi.getConversations();
      const conversationsData = response.data.data || [];
      setConversations(conversationsData);
    } catch (error) {
      console.error('Error cargando conversaciones:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadConversation = async (userId: number) => {
    try {
      const response = await messagesApi.getConversation(userId);
      const messages = response.data.data || [];
      setConversationMessages(messages);

      // Marcar mensajes no leídos como leídos
      const unreadMessages = messages.filter((msg: Message) =>
        !msg.leido && msg.toUserId === user.id
      );

      for (const msg of unreadMessages) {
        await messagesApi.markAsRead(msg.id);
      }

      if (unreadMessages.length > 0 && onMessageRead) {
        onMessageRead();
      }

      // Recargar conversaciones y contador
      loadConversations();
      loadUnreadCount();
    } catch (error) {
      console.error('Error cargando conversación:', error);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const response = await messagesApi.getUnreadCount();
      setUnreadCount(response.data.data.unreadCount);
    } catch (error) {
      console.error('Error cargando contador de no leídos:', error);
    }
  };

  const handleConversationSelect = (conversation: Conversation) => {
    setSelectedConversation(conversation);
    loadConversation(conversation.userId);
  };

  const sendReply = async () => {
    if (!selectedConversation || !replyContent.trim()) return;

    try {
      setSending(true);
      await messagesApi.send({
        toUserId: selectedConversation.userId,
        contenido: replyContent,
        tipo: 'mensaje',
        prioridad: 'normal',
        isReply: true
      });

      setReplyContent('');

      // Recargar la conversación actual
      loadConversation(selectedConversation.userId);
      loadConversations();
    } catch (error) {
      console.error('Error enviando respuesta:', error);
      alert('Error enviando respuesta: ' + (error as any)?.response?.data?.message);
    } finally {
      setSending(false);
    }
  };

  const deleteConversation = async () => {
    if (!selectedConversation) return;

    if (!confirm(`¿Estás seguro de que quieres eliminar toda la conversación con ${selectedConversation.user.name}? Esta acción no se puede deshacer.`)) {
      return;
    }

    try {
      setDeleting(true);
      await messagesApi.deleteConversation(selectedConversation.userId);

      // Remover la conversación de la lista
      setConversations(prev => prev.filter(conv => conv.userId !== selectedConversation.userId));
      setSelectedConversation(null);
      setConversationMessages([]);

      alert('Conversación eliminada exitosamente');
    } catch (error) {
      console.error('Error eliminando conversación:', error);
      alert('Error eliminando conversación: ' + (error as any)?.response?.data?.message);
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

    if (diffHours < 1) {
      const diffMins = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
      return `hace ${diffMins} min`;
    } else if (diffHours < 24) {
      return `hace ${diffHours}h`;
    } else {
      return date.toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'short'
      });
    }
  };

  const formatMessageTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f4f6',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }} />
          <p style={{ color: '#6b7280' }}>Cargando conversaciones...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem',
        paddingBottom: '1rem',
        borderBottom: '2px solid #e5e7eb'
      }}>
        <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>
          💬 Mensajes
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {unreadCount > 0 && (
            <span style={{
              backgroundColor: '#ef4444',
              color: 'white',
              padding: '0.25rem 0.75rem',
              borderRadius: '20px',
              fontSize: '0.875rem',
              fontWeight: '500'
            }}>
              {unreadCount} nuevos
            </span>
          )}
          <button
            onClick={loadConversations}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            🔄 Actualizar
          </button>
        </div>
      </div>

      {conversations.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          backgroundColor: '#f9fafb',
          borderRadius: '12px',
          border: '2px dashed #d1d5db'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
          <h3 style={{ color: '#374151', marginBottom: '0.5rem' }}>No tienes conversaciones</h3>
          <p style={{ color: '#6b7280' }}>Cuando recibas mensajes aparecerán aquí</p>
        </div>
      ) : (
        <div className="messages-container" style={{ display: 'flex', height: '600px', border: '1px solid #d1d5db', borderRadius: '12px', overflow: 'hidden' }}>

          {/* Lista de Conversaciones - Izquierda */}
          <div className={`conversations-sidebar ${selectedConversation ? 'has-selection' : ''}`} style={{
            width: '350px',
            borderRight: '1px solid #e5e7eb',
            backgroundColor: '#f9fafb',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{
              padding: '1rem',
              borderBottom: '1px solid #e5e7eb',
              backgroundColor: 'white',
              fontWeight: '600',
              color: '#374151'
            }}>
              Conversaciones ({conversations.length})
            </div>

            <div style={{ flex: 1, overflowY: 'auto' }}>
              {conversations.map((conversation) => (
                <div
                  key={conversation.userId}
                  onClick={() => handleConversationSelect(conversation)}
                  style={{
                    padding: '1rem',
                    borderBottom: '1px solid #f3f4f6',
                    cursor: 'pointer',
                    backgroundColor: selectedConversation?.userId === conversation.userId ? '#dbeafe' :
                                    conversation.unreadCount > 0 ? '#fef3c7' : 'white',
                    borderLeft: selectedConversation?.userId === conversation.userId ? '4px solid #3b82f6' :
                                conversation.unreadCount > 0 ? '4px solid #f59e0b' : '4px solid transparent',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: 0 }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        fontSize: '1.125rem',
                        flexShrink: 0
                      }}>
                        {conversation.user?.name?.charAt(0).toUpperCase() || '?'}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontWeight: conversation.unreadCount > 0 ? '600' : '500',
                          color: '#1f2937',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          marginBottom: '0.25rem'
                        }}>
                          {conversation.user?.name || 'Usuario eliminado'}
                        </div>
                        <div style={{
                          fontSize: '0.75rem',
                          color: '#6b7280',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {conversation.user?.role || '-'}
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '0.5rem' }}>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                        {formatDate(conversation.lastMessage.createdAt)}
                      </div>
                      {conversation.unreadCount > 0 && (
                        <div style={{
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          borderRadius: '50%',
                          width: '20px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          marginLeft: 'auto'
                        }}>
                          {conversation.unreadCount}
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={{
                    fontSize: '0.875rem',
                    color: '#4b5563',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    paddingLeft: '3rem'
                  }}>
                    {conversation.lastMessage.fromUserId === user.id && '📤 '}
                    {conversation.lastMessage.contenido}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Chat - Derecha */}
          <div className="chat-area" style={{ flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: 'white' }}>
            {selectedConversation ? (
              <>
                {/* Header del chat */}
                <div style={{
                  padding: '1rem 1.5rem',
                  borderBottom: '1px solid #e5e7eb',
                  backgroundColor: '#f8fafc',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    {/* Botón volver (solo móvil) */}
                    <button
                      className="mobile-back-button"
                      onClick={() => setSelectedConversation(null)}
                      style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '1.5rem',
                        cursor: 'pointer',
                        padding: '0.5rem',
                        color: '#3b82f6'
                      }}
                      title="Volver a conversaciones"
                    >
                      ←
                    </button>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '1.125rem'
                    }}>
                      {selectedConversation.user?.name?.charAt(0).toUpperCase() || '?'}
                    </div>
                    <div>
                      <div style={{
                        fontSize: '1rem',
                        fontWeight: '600',
                        color: '#1f2937'
                      }}>
                        {selectedConversation.user?.name || 'Usuario eliminado'}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                        {selectedConversation.user?.role || '-'} • {selectedConversation.user?.email || '-'}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={deleteConversation}
                    disabled={deleting}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: deleting ? '#9ca3af' : '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: deleting ? 'not-allowed' : 'pointer',
                      fontSize: '0.875rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                    title="Eliminar conversación completa"
                  >
                    {deleting ? '⏳' : '🗑️'} Eliminar chat
                  </button>
                </div>

                {/* Mensajes (chat bubbles) */}
                <div
                  ref={messagesContainerRef}
                  style={{
                    flex: 1,
                    padding: '1.5rem',
                    overflowY: 'auto',
                    backgroundColor: '#f9fafb',
                    backgroundImage: 'linear-gradient(180deg, #f9fafb 0%, #ffffff 100%)'
                  }}
                >
                  {conversationMessages.map((message) => {
                    const isSentByMe = message.fromUserId === user.id;

                    return (
                      <div
                        key={message.id}
                        style={{
                          display: 'flex',
                          justifyContent: isSentByMe ? 'flex-end' : 'flex-start',
                          marginBottom: '1rem'
                        }}
                      >
                        <div style={{
                          maxWidth: '70%',
                          backgroundColor: isSentByMe ? '#dcf8c6' : 'white',
                          padding: '0.75rem 1rem',
                          borderRadius: '12px',
                          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                          border: isSentByMe ? 'none' : '1px solid #e5e7eb'
                        }}>
                          {!isSentByMe && (
                            <div style={{
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              color: '#3b82f6',
                              marginBottom: '0.25rem'
                            }}>
                              {message.sender.name}
                            </div>
                          )}
                          <div style={{
                            fontSize: '0.875rem',
                            color: '#1f2937',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                          }}>
                            {message.contenido}
                          </div>
                          <div style={{
                            fontSize: '0.625rem',
                            color: '#6b7280',
                            marginTop: '0.25rem',
                            textAlign: 'right',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: '0.25rem'
                          }}>
                            {formatMessageTime(message.createdAt)}
                            {isSentByMe && message.leido && ' ✓✓'}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Input para enviar mensaje */}
                <div style={{
                  padding: '1rem 1.5rem',
                  borderTop: '1px solid #e5e7eb',
                  backgroundColor: '#f8fafc'
                }}>
                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end' }}>
                    <textarea
                      value={replyContent}
                      onChange={(e) => setReplyContent(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          sendReply();
                        }
                      }}
                      placeholder="Escribe un mensaje..."
                      style={{
                        flex: 1,
                        minHeight: '44px',
                        maxHeight: '120px',
                        padding: '0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '24px',
                        fontSize: '0.875rem',
                        resize: 'none',
                        fontFamily: 'inherit'
                      }}
                    />
                    <button
                      onClick={sendReply}
                      disabled={!replyContent.trim() || sending}
                      style={{
                        padding: '0.75rem 1.5rem',
                        backgroundColor: !replyContent.trim() || sending ? '#9ca3af' : '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '24px',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        cursor: !replyContent.trim() || sending ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {sending ? '📤 ...' : '📨 Enviar'}
                    </button>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
                    Presiona Enter para enviar • Shift+Enter para nueva línea
                  </div>
                </div>
              </>
            ) : (
              <div style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#6b7280'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>💬</div>
                  <p>Selecciona una conversación para empezar</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MessagesEmailView;
